import { z } from 'zod';
/**
 * The permission catalogue (RELAY-934) — ANSI/NIST RBAC: users hold roles, roles hold
 * permissions, a permission is `resource.action`. The catalogue is a closed set so a route
 * cannot declare a permission nobody grants and a role cannot grant one nothing checks;
 * both would be typos that fail at compile time.
 *
 * Adding a permission: add it here, add it to the default roles below (or deliberately to
 * none), and declare it on the route. Nothing else.
 */
export const RESOURCES = [
    'organisation',
    'user',
    'role',
    'team',
    'flag',
    'candidate',
    'customer',
    'job',
    'document',
    'task',
    'field_definition',
    'audit_log',
    'integration',
    'activity',
    'message',
    'automation',
    'note_template',
];
export const PERMISSIONS = [
    'organisation.read',
    'organisation.manage',
    'user.read',
    'user.invite',
    'user.manage',
    /**
     * Move a member's projects and open tasks to another member (TS-1940). `all` reaches anyone;
     * a narrower scope only works as the lead of a team holding both people, and only on someone
     * whose role grants no more than the actor's.
     */
    'user.reassign',
    'role.read',
    'role.manage',
    'team.read',
    'team.manage',
    'flag.read',
    'flag.manage',
    'candidate.read',
    'candidate.create',
    'candidate.update',
    'candidate.delete',
    'customer.read',
    'customer.create',
    'customer.update',
    'customer.delete',
    'job.read',
    'job.create',
    'job.update',
    'job.delete',
    'document.read',
    'document.upload',
    'document.delete',
    /**
     * List other people's tasks (Team Tasks, TS-1743). The scope is over the ASSIGNEE: `owned`
     * is my own, `team` anyone on my team's, `all` the organisation's. A record's tasks and My
     * Tasks do not need it — they follow the record and the assignee.
     */
    'task.read',
    'field_definition.read',
    'field_definition.manage',
    'audit_log.read',
    'integration.read',
    'integration.manage',
    /** Post email and message events onto timelines (TS-955); an integration's key holds it. */
    'activity.ingest',
    /**
     * Feed Message Hub's texts and emails into the CRM (TS-1892): the Supabase job's API key
     * holds it. It writes the message copy only; reading messages follows the record.
     */
    'message.ingest',
    /**
     * Text a client from a record's Messages tab through Twilio (TS-1904). Checked against the
     * record: `owned` reaches a project or contact the actor owns, or a conversation with a
     * contact they own. No API key can hold it (the Integration role does not).
     */
    'message.send',
    /**
     * Pipeline automations: organisation configuration, so checked without a record and a
     * narrower scope reaches every automation. Reading one shows its URL, which can carry the
     * receiver's token.
     */
    'automation.read',
    'automation.create',
    'automation.update',
    'automation.delete',
    /** Create, edit and delete Note templates (organisation configuration, checked without a record). */
    'note_template.manage',
];
export const permissionSchema = z.enum(PERMISSIONS);
/**
 * How far a granted permission reaches:
 *   all   — every record in the organisation;
 *   team  — records owned by anyone on the actor's team (and their own);
 *   owned — only records the actor owns.
 */
export const SCOPES = ['all', 'team', 'owned'];
export const scopeSchema = z.enum(SCOPES);
export const grantSchema = z.object({ permission: permissionSchema, scope: scopeSchema });
const SCOPE_RANK = { owned: 1, team: 2, all: 3 };
/** The wider of two scopes — used when several roles grant the same permission. */
export function widerScope(a, b) {
    return SCOPE_RANK[a] >= SCOPE_RANK[b] ? a : b;
}
/**
 * can(actor, permission, record?) — the ONE answer every route and every UI control uses.
 *
 * Without a record it answers "may this actor do this at all" (a list or create). With a
 * record it applies the grant's scope: `all` always, `team` when the record is on the
 * actor's team or owned by the actor, `owned` only when the actor owns it. A permission the
 * actor does not hold is false whatever the record. Pure, so the web app evaluates the
 * same function on the permission set it receives at sign-in.
 */
export function can(actor, permission, record) {
    const scope = actor.permissions.get(permission);
    if (!scope)
        return false;
    if (!record || scope === 'all')
        return true;
    const owns = (record.ownerId != null && record.ownerId === actor.userId) ||
        (record.responsibleIds?.includes(actor.userId) ?? false);
    if (scope === 'owned')
        return owns;
    const sameTeam = record.teamId != null && actor.teamId != null && record.teamId === actor.teamId;
    return owns || sameTeam;
}
/** Merge grants from several roles into one set, keeping the widest scope per permission. */
export function mergeGrants(grants) {
    const out = new Map();
    for (const g of grants) {
        const current = out.get(g.permission);
        out.set(g.permission, current ? widerScope(current, g.scope) : g.scope);
    }
    return out;
}
const all = (...perms) => perms.map((permission) => ({ permission, scope: 'all' }));
const owned = (...perms) => perms.map((permission) => ({ permission, scope: 'owned' }));
const team = (...perms) => perms.map((permission) => ({ permission, scope: 'team' }));
/**
 * Reads that are NOT part of "read everything": `task.read` is a manager's view of other
 * people's work (TS-1743), `automation.read` shows URLs that can carry a receiver's token, and
 * every role but Admin inherits the Viewer's reads.
 */
const NOT_FOR_EVERYONE = new Set(['task.read', 'automation.read']);
const READ_EVERYTHING = PERMISSIONS.filter((p) => p.endsWith('.read') && !NOT_FOR_EVERYONE.has(p));
export const DEFAULT_ROLES = [
    {
        key: 'viewer',
        name: 'Viewer',
        description: 'Read-only across the organisation. Also what a first sign-in receives until an admin activates the account.',
        grants: all(...READ_EVERYTHING),
    },
    {
        key: 'am_newbie',
        name: 'AM Newbie',
        description: 'A new account manager: read-only across the organisation, the same reach as Viewer, until they are given a working role.',
        // No grants of its own: it stays exactly Viewer's reads if those change.
        inherits: 'viewer',
        grants: [],
    },
    {
        key: 'newcomer',
        name: 'Newcomer',
        description: 'Views candidates, projects and their jobs only, nothing else to work on. Projects share the customer permission, so companies, contacts and deals are readable too; no team tasks, no changes.',
        // Not Viewer: only the reads the candidate and project pages need to load (who owns a
        // record, flags, pipelines/notes/tasks/layouts, custom fields, resumes), plus jobs so the
        // project page's Jobs tab shows.
        grants: all('candidate.read', 'customer.read', 'job.read', 'organisation.read', 'flag.read', 'user.read', 'field_definition.read', 'document.read'),
    },
    {
        key: 'recruiter',
        name: 'Recruiter',
        description: 'Works candidates and their documents on their own team, and the jobs their team is assigned to; reads customers.',
        inherits: 'viewer',
        grants: [
            ...team('candidate.create', 'candidate.update', 'candidate.delete', 'document.upload', 'document.delete', 'job.update'),
        ],
    },
    {
        key: 'account_executive',
        name: 'Account Executive',
        description: 'Owns customer relationships and the jobs they are assigned to (RELAY-950); reads candidates.',
        inherits: 'viewer',
        grants: [
            ...owned('customer.create', 'customer.update', 'customer.delete', 'job.create', 'job.update', 'job.delete', 'document.upload', 'document.delete', 'message.send'),
        ],
    },
    {
        key: 'employee',
        name: 'Employee',
        description: 'Creates and edits candidates, customers, jobs and documents across the organisation; deletes nothing and manages no people or settings. Also what a first sign-in receives while the signup.auto_activate flag is on.',
        inherits: 'viewer',
        grants: [
            ...all('candidate.create', 'candidate.update', 'customer.create', 'customer.update', 'job.create', 'job.update', 'document.upload'),
            // Texting a client goes out under the company's name: only the account manager who owns
            // the contact (and admins) for now (TS-1904).
            ...owned('message.send'),
        ],
    },
    {
        key: 'manager',
        name: 'Manager',
        description: "Everything on candidates and customers across the organisation; sees everyone's tasks; invites users, manages teams and reassigns the work of people in teams they lead.",
        inherits: 'viewer',
        grants: all(
        // `all` until teams can be assigned (nothing sets org_members.team_id yet); then `team`.
        'task.read', 'candidate.create', 'candidate.update', 'candidate.delete', 'customer.create', 'customer.update', 'customer.delete', 'job.create', 'job.update', 'job.delete', 'document.upload', 'document.delete', 'user.invite', 'team.manage', 'field_definition.manage', 'note_template.manage').concat(owned('message.send'), team('user.reassign')),
    },
    {
        key: 'admin',
        name: 'Admin',
        description: 'Every permission, including users, roles, flags and integrations.',
        grants: all(...PERMISSIONS),
    },
    {
        key: 'integration',
        name: 'Integration',
        description: 'For API clients syncing records (RecruitCRM, Stripe): read and write candidates/customers/jobs/documents, nothing about people or settings.',
        grants: all('candidate.read', 'candidate.create', 'candidate.update', 'customer.read', 'customer.create', 'customer.update', 'job.read', 'job.create', 'job.update', 'document.read', 'document.upload', 'field_definition.read', 'activity.ingest', 'message.ingest'),
    },
];
export const DEFAULT_ROLE_KEYS = DEFAULT_ROLES.map((r) => r.key);
export const roleKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
/** Relay's `@relay/auth` role → the CRM default role carrying the same intent. */
export const RELAY_ROLE_COMPAT = {
    admin: 'admin',
    member: 'recruiter',
    viewer: 'viewer',
};
/** Resolve a role's effective grants, following `inherits` (cycle-safe). */
export function effectiveGrants(roleKey, roles = DEFAULT_ROLES) {
    const byKey = new Map(roles.map((r) => [r.key, r]));
    const seen = new Set();
    const out = [];
    let cur = byKey.get(roleKey);
    while (cur && !seen.has(cur.key)) {
        seen.add(cur.key);
        out.push(...cur.grants);
        cur = cur.inherits ? byKey.get(cur.inherits) : undefined;
    }
    return out;
}
/** Account lifecycle (RELAY-934): invited → active → deactivated. Ids are never deleted. */
export const USER_STATUSES = ['invited', 'active', 'deactivated'];
export const userStatusSchema = z.enum(USER_STATUSES);
//# sourceMappingURL=permissions.js.map